// Navigation hub model — the 21-item rail collapses to 8 hubs. Each hub is a
// group of existing routes; the rail shows the hub, and a sub-tab bar (HubTabs)
// navigates between the hub's modules. No page embedding — every route renders
// exactly as before. Module flags + roles filter individual items.

export interface HubItem {
  label: string;
  path: string;
  moduleName?: string;   // gated by the company's enabled modules
  managerOnly?: boolean;
  adminOnly?: boolean;
  superAdminOnly?: boolean; // MoreMe's own super admins (super-super-admin)
}

export interface Hub {
  id: string;
  label: string;
  icon: string;          // key resolved to an MUI icon in NavigationRail
  items: HubItem[];
}

// Optimistic default while /company/modules loads (or fails): every module the
// nav gates on. Shared by NavigationRail + BottomHubBar so they never diverge.
export const FALLBACK_MODULES = [
  'wellness', 'learning', 'microlearning', 'games', 'announcements', 'chat',
  'projects', 'rewards', 'recognition', 'policies', 'analytics', 'livestream',
  'calendar', 'documents', 'cultureHub', 'pulse', 'badges', 'askAria',
  'leave', 'performance',
];

export const HUBS: Hub[] = [
  { id: 'dashboard', label: 'Dashboard', icon: 'home', items: [
    { label: 'Dashboard', path: '/dashboard' },
  ] },
  { id: 'grow', label: 'Grow', icon: 'grow', items: [
    { label: 'Learning', path: '/learning', moduleName: 'learning' },
    // The trail visualiser — everyone's way in. Without this the new paths
    // were only reachable by typing the URL.
    { label: 'My paths', path: '/learning/paths', moduleName: 'learning' },
    { label: 'Microlearning', path: '/microlearning', moduleName: 'microlearning' },
    { label: 'Games', path: '/gamification', moduleName: 'games' },
    { label: 'Build courses', path: '/admin/lms/courses', moduleName: 'learning', adminOnly: true },
    { label: 'Build journeys', path: '/admin/lms/journeys', moduleName: 'learning', adminOnly: true },
    { label: 'Build paths', path: '/admin/lms/learning-paths', moduleName: 'learning', adminOnly: true },
    { label: 'MoreMe courses', path: '/admin/moreme-courses', superAdminOnly: true },
  ] },
  { id: 'wellbeing', label: 'Wellbeing', icon: 'wellbeing', items: [
    { label: 'Wellness', path: '/wellness', moduleName: 'wellness' },
    // Anonymous team trends for anyone who manages people.
    { label: 'Team mood', path: '/wellness/team-mood', moduleName: 'wellness', managerOnly: true },
    { label: 'Mood analytics', path: '/admin/mood-analytics', superAdminOnly: true },
    { label: 'Culture', path: '/culture', moduleName: 'cultureHub' },
  ] },
  { id: 'performance', label: 'Performance', icon: 'performance', items: [
    { label: 'My Performance', path: '/performance', moduleName: 'performance' },
    { label: 'Team', path: '/manager/performance', moduleName: 'performance', managerOnly: true },
    { label: 'Reports', path: '/performance/reports', moduleName: 'performance', managerOnly: true },
    { label: 'Admin', path: '/admin/performance', moduleName: 'performance', adminOnly: true },
  ] },
  { id: 'rewards', label: 'Rewards', icon: 'rewards', items: [
    { label: 'Rewards', path: '/rewards', moduleName: 'rewards' },
    { label: 'Store', path: '/store', moduleName: 'rewards' },
    { label: 'Recognition', path: '/recognition', moduleName: 'recognition' },
  ] },
  { id: 'workspace', label: 'Workspace', icon: 'workspace', items: [
    { label: 'My Tasks', path: '/tasks', moduleName: 'projects' },
    // Open to everyone: the board list is already scoped to the workstreams
    // a person created or was added to, so a manager-only link just hid
    // members' own boards from them.
    { label: 'Team Board', path: '/tasks/manager', moduleName: 'projects' },
    { label: 'Calendar', path: '/calendar', moduleName: 'calendar' },
    { label: 'Documents', path: '/documents', moduleName: 'policies' },
  ] },
  { id: 'connect', label: 'Connect', icon: 'connect', items: [
    { label: 'Announcements', path: '/announcements', moduleName: 'announcements' },
    { label: 'Chat', path: '/chat', moduleName: 'chat' },
    { label: 'Live Stream', path: '/streaming', moduleName: 'livestream' },
    { label: 'Pulse', path: '/pulse', moduleName: 'chat' },
    { label: 'Host live quiz', path: '/live-quiz/host', managerOnly: true },
  ] },
  { id: 'leave', label: 'Leave', icon: 'leave', items: [
    { label: 'My Leave', path: '/leave', moduleName: 'leave' },
    // Line managers, team leads, HR and admins. Matches the route guards in AppRoutes.
    { label: 'Approvals', path: '/manager/leave', moduleName: 'leave', managerOnly: true },
    { label: 'Team report', path: '/manager/leave-report', moduleName: 'leave', managerOnly: true },
  ] },
];

export interface NavPerms {
  enabledModules: string[];
  isManager: boolean;
  isAdmin: boolean;
  isSuperAdmin?: boolean;
}

export function itemAllowed(item: HubItem, p: NavPerms): boolean {
  if (item.moduleName && !p.enabledModules.includes(item.moduleName)) return false;
  if (item.managerOnly && !p.isManager) return false;
  if (item.adminOnly && !p.isAdmin) return false;
  if (item.superAdminOnly && !p.isSuperAdmin) return false;
  return true;
}

export function visibleItems(hub: Hub, p: NavPerms): HubItem[] {
  return hub.items.filter((i) => itemAllowed(i, p));
}

// Hubs the user can actually see (≥1 visible item).
export function visibleHubs(p: NavPerms): Hub[] {
  return HUBS.filter((h) => visibleItems(h, p).length > 0);
}

// The hub that owns the current route (longest path-prefix match wins so
// /admin/performance maps to Performance, not a shorter accidental match).
export function hubForPath(pathname: string): Hub | null {
  let best: Hub | null = null;
  let bestLen = -1;
  for (const hub of HUBS) {
    for (const item of hub.items) {
      if ((pathname === item.path || pathname.startsWith(item.path + '/')) && item.path.length > bestLen) {
        best = hub; bestLen = item.path.length;
      }
    }
  }
  return best;
}

// Landing route for a hub = its first visible item.
export function defaultPath(hub: Hub, p: NavPerms): string | null {
  const items = visibleItems(hub, p);
  return items.length ? items[0].path : null;
}

// Hub items the mobile bottom bar shows directly, in this order; every other
// visible hub goes behind "More". Five slots in all keeps each tap target
// about 72px wide on a 360px phone with a readable label.
export const PRIMARY_HUB_IDS = ['dashboard', 'grow', 'connect', 'workspace'];

// Screens outside the hubs that are destinations in their own right, so the
// mobile top bar gives them a title and no back arrow.
const TOP_LEVEL_TITLES: Record<string, string> = {
  '/profile': 'Profile',
  '/settings': 'Settings',
  '/insights': 'Insights',
  '/admin': 'Admin Hub',
  '/admin/super': 'Super Admin',
  '/notifications': 'Notifications',
};

// The hub item that owns a route (longest path-prefix match).
function itemForPath(pathname: string): HubItem | null {
  let best: HubItem | null = null;
  for (const hub of HUBS) {
    for (const item of hub.items) {
      if ((pathname === item.path || pathname.startsWith(item.path + '/'))
          && (!best || item.path.length > best.path.length)) {
        best = item;
      }
    }
  }
  return best;
}

/**
 * What the mobile top bar shows for a route: a title, and whether the route
 * sits below one people navigate to directly — in which case it gets a back
 * arrow pointing at `parent`. The installed app has no browser back button,
 * so without this a course, task or admin sub-page was a dead end.
 */
export function mobileHeaderFor(pathname: string): { title: string; isChild: boolean; parent: string } {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  if (TOP_LEVEL_TITLES[path]) {
    return { title: TOP_LEVEL_TITLES[path], isChild: false, parent: '/dashboard' };
  }
  const item = itemForPath(path);
  if (item) {
    const hub = hubForPath(path);
    // On a hub's own tab the bar names the hub; the tab row below it names the tab.
    if (item.path === path) {
      return { title: hub && hub.items.length > 1 ? hub.label : item.label, isChild: false, parent: '/dashboard' };
    }
    return { title: item.label, isChild: true, parent: item.path };
  }
  if (path.startsWith('/admin/')) return { title: 'Admin', isChild: true, parent: '/admin' };
  const top = Object.keys(TOP_LEVEL_TITLES).find((p) => path.startsWith(p + '/'));
  if (top) return { title: TOP_LEVEL_TITLES[top], isChild: true, parent: top };
  return { title: '', isChild: path !== '/dashboard', parent: '/dashboard' };
}
